import { Box, Flex, Heading } from 'theme-ui';
import React from 'react';
import { AvailableShiftForRider } from '@velove/types';

import Button from '../Button';
import CentredSpinner from '../CentredSpinner';
import Dialog from '../Dialog';
import ErrorMessage from '../ErrorMessage';
import { IconName } from '../Icon';

import ShiftCard, { ShiftCardProps } from './ShiftCard';

export interface AvailableShiftCardProps extends Omit<ShiftCardProps, 'actions' | 'status'> {
  availableShift: AvailableShiftForRider;
  onRequestShift(): void;
  onUnrequestShift(): void;
  onConfirmUnrequestShift(): void;
  onCancelUnrequestShift(): void;
  requestButton: {
    label: React.ReactNode;
    icon?: IconName;
  };
  requestShiftState: {
    loading: boolean;
    error?: Error;
  };
  unrequestButton: {
    label: React.ReactNode;
  };
  unrequestShiftState: {
    loading: boolean;
    error?: Error;
  };
  confirmationDialog: {
    show: boolean;
    title: React.ReactNode;
    approvalLabel: React.ReactNode;
    denialLabel: React.ReactNode;
  };
}

const AvailableShiftCard: React.FC<AvailableShiftCardProps> = ({
  availableShift,
  confirmationDialog,
  onRequestShift,
  onUnrequestShift,
  onConfirmUnrequestShift,
  onCancelUnrequestShift,
  requestButton,
  requestShiftState,
  unrequestButton,
  unrequestShiftState,
  ...props
}) => (
  <React.Fragment>
    <ShiftCard
      {...props}
      actions={
        <React.Fragment>
          <Flex sx={{ justifyContent: 'center', alignItems: 'center', mt: 'xs', mb: 's' }}>
            {availableShift.shift ? (
              <Button variant="outlineSystemBlueLight" sx={{ flex: 1, boxShadow: 'none' }} onClick={onUnrequestShift}>
                {unrequestButton.label}
              </Button>
            ) : (
              <Button
                variant="primaryShiftBlue"
                disabled={requestShiftState.loading}
                leadingIcon={requestButton.icon ? requestButton.icon : 'Wave'}
                loading={requestShiftState.loading}
                onClick={onRequestShift}
                sx={{ flex: 1, boxShadow: 'none' }}
              >
                {requestButton.label}
              </Button>
            )}
          </Flex>
          {requestShiftState.error && <ErrorMessage padding="s">{requestShiftState.error.message}</ErrorMessage>}
        </React.Fragment>
      }
      status="AVAILABLE"
      availableShift={availableShift}
    />
    {confirmationDialog.show && (
      <Dialog onClose={onCancelUnrequestShift}>
        <Heading as="h4" variant="heading2" sx={{ mt: 0, mb: 'xs' }}>
          {confirmationDialog.title}
        </Heading>
        {unrequestShiftState.loading && <CentredSpinner />}
        {unrequestShiftState.error && <ErrorMessage>{unrequestShiftState.error.message}</ErrorMessage>}
        <Box sx={{ mt: 's' }}>
          <Button
            disabled={unrequestShiftState.loading}
            onClick={onConfirmUnrequestShift}
            sx={{ mr: 's' }}
            variant="roundedDarkBlue"
          >
            {confirmationDialog.approvalLabel}
          </Button>
          <Button variant="textDarkBlue" onClick={onCancelUnrequestShift}>
            {confirmationDialog.denialLabel}
          </Button>
        </Box>
      </Dialog>
    )}
  </React.Fragment>
);

export default AvailableShiftCard;
